import { Button, Card, Container, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { RentalGpsDevice, RentalTrailer, RentalTrailerEvent } from "src/models";
import { isRentalTruckType, rentalTrailerType } from "src/constants";
import useModelData, { deleteById, replaceById } from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";

import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { paths } from "src/routes/paths";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import { RentalTrailerModal } from "./rental-trailer-modal";


type RentalTrailersProps = {
  rentalTrailers?: RentalTrailer[],
  unitLabel: string,
  onRentalTrailerSelected: (a: RentalTrailer) => void,
  onRentalTrailerHistory: (a: RentalTrailer) => void
}

const RentalTrailers = memo(({ rentalTrailers, unitLabel, onRentalTrailerSelected, onRentalTrailerHistory }: RentalTrailersProps) => {
  const { sortedList, sortData } = useSort(rentalTrailers, "trailerUnitNumber");

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["trailerUnitNumber","make","model","year","vin","licensePlateState","licensePlate"]);

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor={`${unitLabel}s`} />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
            <SortableHeader sortKey="trailerUnitNumber" sortData={sortData} >
                Unit Number
              </SortableHeader>

              <SortableHeader sortKey="make" sortData={sortData} >
                Make
              </SortableHeader>

              <SortableHeader sortKey="model" sortData={sortData} >
                Model
              </SortableHeader>

              <SortableHeader sortKey="year" sortData={sortData} >
                Year
              </SortableHeader>

              <SortableHeader sortKey="trailerType" sortData={sortData} >
                Type
              </SortableHeader>

              <SortableHeader sortKey="vin" sortData={sortData} >
                VIN
              </SortableHeader>

              <SortableHeader sortKey="licensePlate" sortData={sortData} >
                Plate Number
              </SortableHeader>

              <SortableHeader sortKey="licensePlateState" sortData={sortData} >
                Plate State
              </SortableHeader>

              <TruxTableCell>
                Rentals
              </TruxTableCell>
            </TableRow>
          </TableHead>


          <TableBody>
            {filteredList.map((rentalTrailer, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onRentalTrailerSelected(rentalTrailer)}
              sx={{ cursor: "pointer" }}
            >

<TruxTableCell>
                {rentalTrailer.trailerUnitNumber}
              </TruxTableCell>
              <TruxTableCell>
                {rentalTrailer.make}
              </TruxTableCell>
              <TruxTableCell>
                {rentalTrailer.model}
              </TruxTableCell>
              <TruxTableCell>
                {rentalTrailer.year}
              </TruxTableCell>
              <TruxTableCell>
                {rentalTrailerType(rentalTrailer.trailerType)?.label || "-"}
              </TruxTableCell>
              <TruxTableCell>
                {rentalTrailer.vin}
              </TruxTableCell>
              <TruxTableCell>
                {rentalTrailer.licensePlate}
              </TruxTableCell>
              <TruxTableCell>
                {rentalTrailer.licensePlateState}
              </TruxTableCell>
              <TruxTableCell>
                <Button
                  size="small"
                  variant="soft"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRentalTrailerHistory(rentalTrailer);
                  }}
                >
                  History
                </Button>
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  rentalTrailers?: RentalTrailer[],
  devices?: RentalGpsDevice[],
  events?: RentalTrailerEvent[],
}

export default function Page({ truck = false }: { truck?: boolean }) {
  const settings = useSettingsContext();
  const router = useRouter();
  const unitLabel = truck ? "Truck" : "Trailer";
  const dataLoader = useCallback(() => ({
    rentalTrailers: axios.get(endpoints.rentalTrailers.findAll).then(response =>
      (response.data as RentalTrailer[]).filter(trailer => isRentalTruckType(trailer.trailerType) === truck)
    ),
    devices: axios.get(endpoints.rentalTrailers.gpsDevices).then(response =>
      (response.data as RentalGpsDevice[]).filter(device => device.deviceType.startsWith("ELD Truck #") === truck)
    ),
    events: axios.get(endpoints.rentalTrailerEvents.findAll).then((response) => response.data),
  }), [truck]);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      alert(error);
    }
  }, [loading, error]);

  const [isOpen, setIsOpen] = useState(false);

  const selectedRentalTrailerModel = useModelSavingData<RentalTrailer>(
    endpoints.rentalTrailers.update,
    endpoints.rentalTrailers.create
  );

  const updateRentalTrailer = () => {
    selectedRentalTrailerModel.save().then(response => {
      if (response) {
        enqueueSnackbar(`Successfully saved the ${unitLabel}`);
        const rentalTrailer = response.data as RentalTrailer;

        if (!rentalTrailer.active) {
          updateData("rentalTrailers")(deleteById(data!.rentalTrailers!, rentalTrailer))
        } else {
          updateData("rentalTrailers")(replaceById(data!.rentalTrailers!, rentalTrailer));
        }

        setIsOpen(false);
      }
    })
  }

  const onRentalTrailerSelected = useCallback((a: RentalTrailer) => {
    selectedRentalTrailerModel.set({
      ...a
    });
    setIsOpen(true);
  }, [selectedRentalTrailerModel, setIsOpen]);


  const onNewRentalTrailerSelected = useCallback(() => {
    selectedRentalTrailerModel.set({
      id: 0,
      createdAt: undefined,
      active: true,
      organizationId: 0,

      trailerUnitNumber: "0",
      make: "",
      model: "",
      year: "",
      vin: "",
      licensePlateState: "",
      licensePlate: "",
      trailerType: truck ? "bobtail" : "",
      deviceId: 0,

      registrationFile: "",
      insuranceFile: "",
      inspectionFile: "",
      liabilityInsuranceFile: "",
      workLog: [],
      photoUrl: ""
    });
    setIsOpen(true);
  }, [selectedRentalTrailerModel, truck]);

  const newRentalTrailerButton = <Button
    onClick={onNewRentalTrailerSelected}
    variant="contained"
    color="success"
    startIcon={<Iconify icon="mingcute:add-line" />}
  >
    New {unitLabel}
  </Button>;

const inactiveRentalTrailersButton = <Button
  onClick={() => router.push(truck ? paths.app.rentals.trucks.inactive : paths.app.rentals.trailers.inactive)}
  variant="soft"
>
  Inactive {unitLabel}s
</Button>;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.rentalTrailers?.length !== 0 &&
          <PageHeader title={`${unitLabel}s`}>
            {inactiveRentalTrailersButton}
            {newRentalTrailerButton}
          </PageHeader>
        }

        {data && data?.rentalTrailers?.length === 0 &&
          <NoneFound title={`No Active ${unitLabel}s Found`} message={`To add a ${unitLabel} click the 'New ${unitLabel}' button below.`}>
            {inactiveRentalTrailersButton}
            {newRentalTrailerButton}
          </NoneFound>
        }

        {data && data?.rentalTrailers?.length !== 0 && <RentalTrailers rentalTrailers={data.rentalTrailers} unitLabel={unitLabel} onRentalTrailerSelected={onRentalTrailerSelected} onRentalTrailerHistory={(rentalTrailer) => router.push(paths.app.rentals.trailers.single(rentalTrailer.id))} />}

        {data && <RentalTrailerModal
          selectedRentalTrailerModel={selectedRentalTrailerModel}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          updateRentalTrailer={updateRentalTrailer}
          devices={data.devices}
          rentalTrailers={data.rentalTrailers}
          events={data.events}
          truck={truck}
        />}
      </DisplayPageData>
    </Container >
  );
}
